import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Upcoming {
  id: string; vehicle_id: string; vehicle_code: string; vehicle_plate: string; brand: string | null; model: string | null;
  event_type: string; interval_km: number | null; interval_days: number | null;
  last_done_km: number | null; last_done_date: string | null;
  next_due_km: number | null; next_due_date: string | null;
  is_active: boolean;
}

export default function ServiceSchedulePage() {
  const [items, setItems] = useState<Upcoming[]>([]);
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Upcoming[]>(`/api/v1/maintenance/schedule/upcoming?days=${days}`);
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  const overdue = items.filter(i => i.next_due_date && new Date(i.next_due_date) < new Date());
  const upcoming = items.filter(i => i.next_due_date && new Date(i.next_due_date) >= new Date());

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> Service schedule</div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="7">Next 7 days</option>
            <option value="14">Next 14 days</option>
            <option value="30">Next 30 days</option>
            <option value="90">Next 90 days</option>
          </select>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : (
          <>
            {overdue.length > 0 && (
              <section>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-2"><AlertTriangle className="h-3 w-3 text-rose-600" /> Overdue ({overdue.length})</div>
                <ScheduleTable items={overdue} tone="rose" />
              </section>
            )}
            <section>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Upcoming ({upcoming.length})</div>
              {upcoming.length === 0 ? <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">Nothing due soon.</div>
                : <ScheduleTable items={upcoming} />}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function ScheduleTable({ items, tone }: { items: Upcoming[]; tone?: 'rose' }) {
  return (
    <div className={'rounded-lg border overflow-hidden ' + (tone === 'rose' ? 'border-rose-500/30' : 'border-border')}>
      <table className="min-w-full text-sm">
        <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
          <tr><th className="text-left px-3 py-2">Vehicle</th><th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">Last done</th><th className="text-left px-3 py-2">Next due</th>
              <th className="text-left px-3 py-2">Interval</th></tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2 text-sm">{it.vehicle_code} · {it.vehicle_plate} {it.brand ? `· ${it.brand} ${it.model ?? ''}` : ''}</td>
              <td className="px-3 py-2 text-xs">{it.event_type.replace(/_/g, ' ')}</td>
              <td className="px-3 py-2 text-xs">{it.last_done_date ?? '—'}{it.last_done_km ? ` · ${it.last_done_km.toLocaleString()} km` : ''}</td>
              <td className="px-3 py-2 text-xs">{it.next_due_date ?? '—'}{it.next_due_km ? ` · ${it.next_due_km.toLocaleString()} km` : ''}</td>
              <td className="px-3 py-2 text-xs">{it.interval_km ? `${it.interval_km.toLocaleString()} km` : ''}{it.interval_km && it.interval_days ? ' · ' : ''}{it.interval_days ? `${it.interval_days} days` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
